export {};

type Locale = "fr" | "en";

declare global {
  interface Navigator {
    connection?: { saveData?: boolean; effectiveType?: string };
  }
  interface Window {
    turnstile?: { reset(widget?: string | HTMLElement): void };
  }
}

const locale: Locale = document.documentElement.lang === "en" ? "en" : "fr";
const ui = {
  fr: {
    invalidEmail: "Saisissez une adresse e-mail valide.",
    consent: "Cochez la case de consentement pour poursuivre.",
    turnstile: "Terminez la vérification anti-robot avant de poursuivre.",
    network: "La connexion au service d’inscription a échoué. Réessayez.",
    processing: "Inscription en cours…",
  },
  en: {
    invalidEmail: "Enter a valid email address.",
    consent: "Select the consent checkbox to continue.",
    turnstile: "Complete the anti-bot check before continuing.",
    network: "The subscription service could not be reached. Please try again.",
    processing: "Submitting…",
  },
} as const;

function updateYear(): void {
  document.querySelectorAll<HTMLElement>("[data-current-year]").forEach((node) => {
    node.textContent = String(new Date().getFullYear());
  });
}

function shouldUseStaticMedia(): boolean {
  const connection = navigator.connection;
  return (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    connection?.saveData === true ||
    connection?.effectiveType === "slow-2g" ||
    connection?.effectiveType === "2g"
  );
}

function addSource(video: HTMLVideoElement, src: string, type: string): void {
  const source = document.createElement("source");
  source.src = src;
  source.type = type;
  video.appendChild(source);
}

function initialiseVideo(): void {
  const video = document.querySelector<HTMLVideoElement>("[data-hero-video]");
  if (!video) return;
  if (shouldUseStaticMedia()) {
    document.documentElement.classList.add("media-static");
    video.remove();
    return;
  }

  const mobile = window.matchMedia("(max-width: 48rem)").matches;
  const poster = mobile ? video.dataset.mobilePoster : video.dataset.desktopPoster;
  if (poster) video.poster = poster;
  const webm = mobile ? video.dataset.mobileWebm : video.dataset.desktopWebm;
  const mp4 = mobile ? video.dataset.mobileMp4 : video.dataset.desktopMp4;
  if (webm) addSource(video, webm, "video/webm");
  if (mp4) addSource(video, mp4, "video/mp4");
  if (!webm && !mp4) {
    document.documentElement.classList.add("media-static");
    return;
  }

  video.addEventListener("playing", () => document.documentElement.classList.add("media-playing"), {
    once: true,
  });
  video.addEventListener("error", () => document.documentElement.classList.add("media-static"));
  video.load();
  void video.play().catch(() => document.documentElement.classList.add("media-static"));

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) video.pause();
    else if (!shouldUseStaticMedia()) void video.play().catch(() => undefined);
  });
}

function getTurnstileToken(form: HTMLFormElement): string {
  const field = form.elements.namedItem("cf-turnstile-response");
  return field instanceof HTMLInputElement ? field.value.trim() : "";
}

function setStatus(
  node: HTMLElement,
  message: string,
  state: "idle" | "loading" | "success" | "error",
): void {
  node.textContent = message;
  node.dataset.state = state;
}

function setFieldError(field: HTMLInputElement, node: HTMLElement | null, message: string): void {
  field.setAttribute("aria-invalid", message ? "true" : "false");
  if (!node) return;
  node.textContent = message;
  node.hidden = !message;
}

function initialiseForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-subscribe-form]");
  const status = document.querySelector<HTMLElement>("[data-form-status]");
  if (!form || !status) return;

  // JavaScript provides localised, accessible field messages; native validation remains the no-JS fallback.
  form.noValidate = true;

  const email = form.elements.namedItem("email");
  const consent = form.elements.namedItem("consent");
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const emailError = document.querySelector<HTMLElement>("[data-email-error]");
  const consentError = document.querySelector<HTMLElement>("[data-consent-error]");
  if (!(email instanceof HTMLInputElement) || !(consent instanceof HTMLInputElement) || !submit) {
    return;
  }

  email.addEventListener("input", () => setFieldError(email, emailError, ""));
  consent.addEventListener("change", () => setFieldError(consent, consentError, ""));

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const messages = ui[locale];
    setStatus(status, "", "idle");
    setFieldError(email, emailError, "");
    setFieldError(consent, consentError, "");

    if (!email.value.trim() || !email.validity.valid) {
      setFieldError(email, emailError, messages.invalidEmail);
      email.focus();
      return;
    }
    if (!consent.checked) {
      setFieldError(consent, consentError, messages.consent);
      consent.focus();
      return;
    }

    const token = getTurnstileToken(form);
    if (!token) {
      setStatus(status, messages.turnstile, "error");
      document.querySelector<HTMLElement>("[data-turnstile-shell]")?.focus();
      return;
    }

    const data = new FormData(form);
    submit.disabled = true;
    submit.setAttribute("aria-busy", "true");
    setStatus(status, messages.processing, "loading");

    try {
      const response = await fetch(form.action, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-LudoDot-Locale": locale,
        },
        body: JSON.stringify({
          email: email.value,
          consent: consent.checked,
          locale: data.get("locale") === "en" ? "en" : "fr",
          company: String(data.get("company") ?? ""),
          turnstileToken: token,
        }),
      });
      const payload = (await response.json()) as { ok?: boolean; message?: string };
      if (!response.ok || !payload.ok) {
        setStatus(status, payload.message || messages.network, "error");
        window.turnstile?.reset();
        return;
      }
      setStatus(status, payload.message || messages.network, "success");
      form.dataset.state = "success";
      email.disabled = true;
      consent.disabled = true;
      submit.hidden = true;
      document.querySelector<HTMLElement>("[data-turnstile-shell]")?.setAttribute("hidden", "");
    } catch {
      setStatus(status, messages.network, "error");
      window.turnstile?.reset();
    } finally {
      submit.disabled = false;
      submit.removeAttribute("aria-busy");
    }
  });
}

document.documentElement.classList.add("js");
updateYear();
initialiseVideo();
initialiseForm();
